import { useAuth } from '@/hooks/use-auth'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus, Route as RouteIcon, AlertTriangle, Clock, MapPin, Inbox } from 'lucide-react'
import { toast } from 'sonner'
import { Page, PageHeader, TabPills, StatusPill, statusTone, btnPrimary, btnSecondary, btnGhost, Combobox } from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { useSchedule, useScheduleRange, type ScheduleJob } from '@/hooks/use-schedule'
import { todayIso, dayIso, addDaysIso as addDays, addMonthsIso, startOfWeekIso, isoToDate, nextAutoDate } from '@/lib/today'
import { useTeam } from '@/hooks/use-company'
import { useClients, clientName } from '@/hooks/use-clients'
import { cn } from '@/lib/utils'
import { phFormat, phDay, manilaWallToIso, manilaDayIso, toInputTime } from '@/lib/ph'

export const Route = createFileRoute('/_app/schedule')({ component: SchedulePage })

const UNASSIGNED = '__none__'
/** Migrated jobs keep the street in line1 with `address` empty — read whatever exists. */
const schedAddress = (j: ScheduleJob) => {
  const street = [j.line1, j.line2].filter(Boolean).join(' ').trim()
  const region = [j.city, j.state, j.postal_code].filter(Boolean).join(' ').trim()
  if (street) return [street, region].filter(Boolean).join(', ')
  return (j.address ?? '').trim() || region
}
const schedClient = (j: ScheduleJob) =>
  [j.contact?.first_name, j.contact?.last_name].filter(Boolean).join(' ').trim() || j.contact?.organization || ''
/** Scope and price at a glance; clamped to two lines so a long note can't swamp the day. */
const schedNote = (j: ScheduleJob) => (j.task_field_value ?? [])
  .filter((v) => v.value_text?.trim() && (v.field?.type === 'paragraph' || v.field?.type === 'text'))
  .sort((a, b) => (a.field?.row ?? 0) - (b.field?.row ?? 0))
  .map((v) => v.value_text!.trim())[0] ?? ''

const startOfWeekD = (iso: string) => isoToDate(startOfWeekIso(iso))
const addD = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const fmtDay = (iso: string) => phFormat(phDay(iso), 'date', { weekday: 'long', day: 'numeric', month: 'long' })
const fmtTime = (iso: string | null) => (iso ? phFormat(new Date(iso), 'time', { hour: 'numeric', minute: '2-digit' }) : '—')
const durLabel = (j: ScheduleJob) => {
  if (!j.planned_start_date || !j.planned_end_date) return ''
  const m = Math.round((new Date(j.planned_end_date).getTime() - new Date(j.planned_start_date).getTime()) / 60000)
  if (m <= 0) return '' // bad data (end before start) — never render a negative duration
  return m >= 60 ? `${(m / 60).toFixed(m % 60 ? 1 : 0)}h` : `${m}m`
}
type ViewMode = 'day' | 'week' | 'month'

function SchedulePage() {
  const navigate = useNavigate()
  const [date, setDate] = useState(todayIso())
  const [view, setView] = useState<ViewMode>('day')

  // `useState(todayIso())` runs once, at mount. Nobody closes this tab — it is the screen the
  // office leaves open all day — so the morning after, the page was still showing yesterday, and
  // Week was still showing last week because its range is derived from `date`. Reported by a
  // customer on 2026-08-24.
  //
  // `autoDay` is the date we put there ourselves. While `date` still equals it the user has not
  // navigated, so we are free to roll it forward. Once they click to another day the two differ
  // and we leave their choice alone — a look at next week must not be yanked back to today.
  const autoDay = useRef(date)
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'visible') return
      setDate((d) => {
        const next = nextAutoDate(d, autoDay.current, todayIso())
        autoDay.current = next.auto
        return next.date
      })
    }
    // Checked on a timer as well as on focus: the schedule is often on a second screen that is
    // visible but never focused, and midnight still has to move it.
    const tick = setInterval(sync, 60_000)
    document.addEventListener('visibilitychange', sync)
    window.addEventListener('focus', sync)
    return () => {
      clearInterval(tick)
      document.removeEventListener('visibilitychange', sync)
      window.removeEventListener('focus', sync)
    }
  }, [])
  const { user, role } = useAuth()
  const isAdmin = role === 'company-admin'
  const [tech, setTech] = useState(role === 'company-admin' ? '' : (user?.id ?? ''))   // '' = all, userId, or UNASSIGNED
  const { members: allMembers } = useTeam()
  const members = isAdmin ? allMembers : allMembers.filter((m) => m.id === user?.id)
  const { clients } = useClients()

  // Day board data (single day)
  const { jobs, backlog, isLoading, assign, quickAdd } = useSchedule(date)
  const [dragId, setDragId] = useState<number | null>(null)
  const [edit, setEdit] = useState<ScheduleJob | null>(null)
  const [adding, setAdding] = useState(false)

  // Range data (week / month)
  const weekStart = startOfWeekD(date)
  const weekEnd = addD(weekStart, 6)
  const monthFirst = new Date(new Date(`${date}T00:00:00`).getFullYear(), new Date(`${date}T00:00:00`).getMonth(), 1)
  const gridStart = startOfWeekD(dayIso(monthFirst))
  const gridEnd = addD(gridStart, 41)
  const rangeFrom = view === 'week' ? weekStart : gridStart
  const rangeTo = view === 'week' ? weekEnd : gridEnd
  // Manila midnight to Manila end of day (spec 001), not the device's.
  const range = useScheduleRange(manilaWallToIso(dayIso(rangeFrom), '00:00')!, new Date(Date.parse(manilaWallToIso(addDays(dayIso(rangeTo), 1), '00:00')!) - 1).toISOString(), tech || null, view !== 'day')

  const memberName = (uid: string | null) => { if (!uid) return 'Unassigned'; const m = members.find((x) => x.id === uid); return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Tech' : 'Tech' }
  const contactName = (cid: string | null) => { if (!cid) return ''; const c = clients.find((x) => x.id === cid); return c ? clientName(c) : '' }
  const step = (dir: number) => setDate(view === 'day' ? addDays(date, dir) : view === 'week' ? addDays(date, dir * 7) : addMonthsIso(date, dir))
  const label = view === 'day' ? fmtDay(date)
    : view === 'week' ? `${phFormat(phDay(dayIso(weekStart)), 'date', { day: 'numeric', month: 'short' })} – ${phFormat(phDay(dayIso(weekEnd)), 'date', { day: 'numeric', month: 'short' })}`
    : phFormat(phDay(date), 'date', { month: 'long', year: 'numeric' })

  // group range jobs by day iso
  const byDay = new Map<string, ScheduleJob[]>()
  ;(range.data ?? []).forEach((j) => { if (!j.planned_start_date) return; const k = manilaDayIso(j.planned_start_date); (byDay.get(k) ?? byDay.set(k, []).get(k)!).push(j) })

  // ── Day board helpers ──
  const byUser = (uid: string | null) => jobs.filter((j) => j.user_id === uid).sort((a, b) => (a.planned_start_date ?? '').localeCompare(b.planned_start_date ?? ''))
  const conflictIds = new Set<number>()
  members.forEach((m) => { const col = byUser(m.id); for (let i = 1; i < col.length; i++) { const p = col[i - 1], c = col[i]; if (p.planned_end_date && c.planned_start_date && p.planned_end_date > c.planned_start_date) { conflictIds.add(c.id); conflictIds.add(p.id) } } })
  const drop = (userId: string | null) => { if (dragId == null) return; assign.mutate({ id: dragId, user_id: userId }); setDragId(null) }
  const dayMembers = tech && tech !== UNASSIGNED ? members.filter((m) => m.id === tech) : members
  const showBacklog = !tech || tech === UNASSIGNED

  const Column = ({ title, userId, jobs: list, sub }: { title: string; userId: string | null; jobs: ScheduleJob[]; sub?: string }) => (
    <div onDragOver={(e) => e.preventDefault()} onDrop={() => drop(userId)} className="flex w-[260px] flex-none flex-col rounded-[14px] border border-transparent bg-token-surface shadow-[var(--surface-shadow)]">
      <div className="flex items-center justify-between border-b border-token-subtle px-4 py-3">
        <div className="flex items-center gap-2.5">
          {userId === null ? <span className="flex h-7 w-7 items-center justify-center rounded-full bg-canvas-subtle text-token-secondary"><Inbox className="h-3.5 w-3.5" /></span>
            : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-token-selected text-[12px] font-bold text-token-warning">{title[0]?.toUpperCase()}</span>}
          <div><p className="text-[13.5px] font-bold text-token-primary">{title}</p>{sub && <p className="text-[11.5px] text-token-secondary">{sub}</p>}</div>
        </div>
        <span className="text-[12px] font-bold tabular-nums text-token-muted">{list.length}</span>
      </div>
      <div className="flex-1 space-y-2 p-2.5">
        {list.length === 0 && <p className="px-2 py-6 text-center text-[12px] text-token-muted">Drop a job here</p>}
        {list.map((j) => {
          const conflict = conflictIds.has(j.id)
          return (
            // Clicking the card OPENS THE JOB, the same as the week and month grids. It used to
            // open the time/duration modal, so the Day board was the one view you could not reach
            // a job from: a customer had to go to Jobs and search for it to make any edit (2026-08-31).
            // Setting the time keeps its own control below, and dragging still reassigns the job.
            <div key={j.id} draggable onDragStart={() => setDragId(j.id)}
              onClick={() => navigate({ to: '/jobs/$id', params: { id: String(j.id) } })}
              className={cn('cursor-pointer rounded-[10px] border bg-token-surface p-2.5 shadow-[var(--surface-shadow)] transition active:cursor-grabbing hover:border-token-strong', conflict ? 'border-token-danger ring-1 ring-token-danger-surface' : 'border-token-subtle')}>
              <div className="flex items-start justify-between gap-2"><p className="text-[13px] font-semibold leading-snug text-token-primary">{j.name || 'Job'}</p><StatusPill status={j.status ?? 'pending'} /></div>
              <div className="mt-1.5 flex items-center gap-3 text-[11.5px] text-token-secondary">
                {/* The time is the control that changes the time. stopPropagation so it does not
                    also open the job. The backlog column has no time yet, so it says so. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEdit(j) }}
                  title={userId === null ? 'Set a time for this job' : 'Change the time or duration'}
                  className="inline-flex items-center gap-1 rounded-[6px] px-1 py-0.5 -mx-1 hover:bg-canvas-subtle hover:text-token-primary">
                  <Clock className="h-3 w-3" />
                  {userId === null ? 'Set time' : <>{fmtTime(j.planned_start_date)}{durLabel(j) && ` · ${durLabel(j)}`}</>}
                </button>
                {schedAddress(j) && <span className="inline-flex items-center gap-1 truncate"><MapPin className="h-3 w-3" /> {schedAddress(j)}</span>}
              </div>
              {schedClient(j) && <p className="mt-1 truncate text-[11.5px] font-medium text-token-secondary">{schedClient(j)}</p>}
              {schedNote(j) && <p className="mt-1 line-clamp-2 text-[11.5px] leading-snug text-token-primary">{schedNote(j)}</p>}
              {conflict && <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-token-danger"><AlertTriangle className="h-3 w-3" /> Overlaps another job</p>}
            </div>
          )
        })}
      </div>
      {userId !== null && list.length > 0 && (
        <div className="border-t border-token-subtle p-2"><button className={cn(btnGhost, 'w-full justify-center')} onClick={() => { toast.message('Optimising route…'); navigate({ to: '/routes' }) }}><RouteIcon className="h-3.5 w-3.5" /> Optimise day</button></div>
      )}
    </div>
  )

  // Who is on the job, in the smallest space that still reads (a customer, 2026-08-12). The day view
  // already answers this with one column per person, so this is for the week and month grids.
  const initialsOf = (uid: string | null) => {
    if (!uid) return null
    const m = members.find((x) => x.id === uid)
    if (!m) return '?'
    const s = [m.first_name, m.last_name].filter(Boolean).map((n) => n![0]).join('')
    return (s || m.email?.[0] || '?').toUpperCase().slice(0, 2)
  }
  const TechDot = ({ uid }: { uid: string | null }) => {
    const ini = initialsOf(uid)
    const tints = ['#3a6bd6', '#2faa6b', '#8b5cf6', '#cf7a1a', '#e2566c']
    const tint = ini && ini !== '?' ? tints[[...ini].reduce((a, c) => a + c.charCodeAt(0), 0) % tints.length] : '#8a94a6'
    return (
      <span
        title={memberName(uid)}
        aria-label={uid ? `Assigned to ${memberName(uid)}` : 'Unassigned'}
        className="inline-flex h-[15px] min-w-[15px] flex-none items-center justify-center rounded-full px-[3px] text-[8.5px] font-bold leading-none"
        style={uid ? { background: `${tint}22`, color: tint } : { border: '1px dashed var(--border)', color: 'var(--text-muted)' }}
      >{ini ?? '·'}</span>
    )
  }

  const JobChip = ({ j }: { j: ScheduleJob }) => {
    const t = statusTone(j.status ?? 'pending')
    return (
      <button onClick={() => navigate({ to: '/jobs/$id', params: { id: String(j.id) } })} className="block w-full rounded-[8px] border border-token-subtle bg-token-surface px-2 py-1.5 text-left transition hover:border-token-strong">
        <span className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 flex-none rounded-full', t.dot)} />
          <span className="font-display text-[11px] font-bold tabular-nums text-token-secondary">{fmtTime(j.planned_start_date)}</span>
          <span className="truncate text-[12px] font-semibold text-token-primary">{j.name || 'Job'}</span>
          {/* On EVERY card, in every view. The day view answers this with a column per person and
              the week view printed the name, but only when no filter was set, so the owner still
              could not see it (a customer, 2026-08-12, twice). Redundant beside a column header is a
              fair price for never having to wonder. */}
          <span className="ml-auto flex-none"><TechDot uid={j.user_id} /></span>
        </span>
        <span className="mt-0.5 block truncate pl-3.5 text-[11px] text-token-secondary">{contactName(j.contact_id) || '—'}</span>
      </button>
    )
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Schedule"
        title={view === 'day' ? 'Dispatch board' : view === 'week' ? 'Week' : 'Month'}
        subtitle={label}
        actions={
          <>
            <div className="flex items-center rounded-[10px] border border-token-subtle bg-token-surface">
              <button className="px-2 py-2 text-token-secondary hover:text-token-primary" onClick={() => step(-1)}><ChevronLeft className="h-4 w-4" /></button>
              <button className="px-3 py-2 text-[12.5px] font-bold text-token-primary hover:bg-canvas-subtle" onClick={() => setDate(todayIso())}>Today</button>
              <button className="px-2 py-2 text-token-secondary hover:text-token-primary" onClick={() => step(1)}><ChevronRight className="h-4 w-4" /></button>
            </div>
            <TabPills active={view} onChange={(v) => setView(v as ViewMode)} chips={[{ id: 'day', label: 'Day' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' }]} />
            {isAdmin && <Select value={tech} onChange={(e) => setTech(e.target.value)} className="h-9 w-full sm:w-[160px]">
              <option value="">All</option>
              <option value={UNASSIGNED}>Unassigned</option>
              {members.map((m) => <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>)}
            </Select>}
            <button className={btnPrimary} onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> New job</button>
          </>
        }
      />

      {/* DAY */}
      {view === 'day' && (isLoading ? <div className="mt-6 h-64 animate-pulse rounded-[14px] bg-token-surface" /> : (
        <div className="mt-6 flex gap-3 overflow-x-auto pb-4">
          {showBacklog && <Column title="Unscheduled" userId={null} jobs={backlog} sub="Backlog" />}
          {dayMembers.map((m) => {
            const list = byUser(m.id)
            const mins = list.reduce((s, j) => s + (j.planned_start_date && j.planned_end_date ? Math.max(0, (new Date(j.planned_end_date).getTime() - new Date(j.planned_start_date).getTime()) / 60000) : 0), 0)
            return <Column key={m.id} title={memberName(m.id)} userId={m.id} jobs={list} sub={mins ? `${(mins / 60).toFixed(mins % 60 ? 1 : 0)}h booked` : 'Free'} />
          })}
          {members.length === 0 && <div className="flex h-40 w-full items-center justify-center rounded-[14px] border border-dashed border-token text-[13px] text-token-secondary">Add team members under Settings → Team to dispatch work.</div>}
        </div>
      ))}

      {/* WEEK */}
      {view === 'week' && (range.isLoading ? <div className="mt-6 h-64 animate-pulse rounded-[14px] bg-token-surface" /> : (
        <div className="mt-6 overflow-x-auto pb-2">
        <div className="grid min-w-[760px] grid-cols-7 gap-2">
          {Array.from({ length: 7 }, (_, i) => addD(weekStart, i)).map((d) => {
            const k = dayIso(d); const list = (byDay.get(k) ?? []).slice().sort((a, b) => (a.planned_start_date ?? '').localeCompare(b.planned_start_date ?? ''))
            const isToday = k === todayIso()
            return (
              <div key={k} className="flex min-h-[160px] flex-col rounded-[12px] border border-transparent bg-token-surface shadow-[var(--surface-shadow)]">
                <div className={cn('flex items-center justify-between border-b border-token-subtle px-2.5 py-2', isToday && 'bg-token-selected')}>
                  <span className="text-[11.5px] font-bold text-token-primary">{phFormat(phDay(dayIso(d)), 'date', { weekday: 'short' })} <span className="text-token-secondary">{d.getDate()}</span></span>
                  {list.length > 0 && <span className="text-[10.5px] font-bold tabular-nums text-token-muted">{list.length}</span>}
                </div>
                <div className="flex-1 space-y-1.5 p-1.5">
                  {list.length === 0 ? <p className="px-1 py-4 text-center text-[11px] text-token-muted">—</p> : list.map((j) => <JobChip key={j.id} j={j} />)}
                </div>
              </div>
            )
          })}
        </div>
        </div>
      ))}

      {/* MONTH */}
      {view === 'month' && (range.isLoading ? <div className="mt-6 h-64 animate-pulse rounded-[14px] bg-token-surface" /> : (
        <div className="mt-6 overflow-x-auto rounded-[14px] border border-transparent bg-token-surface shadow-[var(--surface-shadow)]">
          <div className="min-w-[720px]">
          <div className="grid grid-cols-7 border-b border-token-subtle bg-canvas-subtle">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 42 }, (_, i) => addD(gridStart, i)).map((d) => {
              const k = dayIso(d); const list = (byDay.get(k) ?? []).slice().sort((a, b) => (a.planned_start_date ?? '').localeCompare(b.planned_start_date ?? ''))
              const inMonth = d.getMonth() === monthFirst.getMonth(); const isToday = k === todayIso()
              return (
                <div key={k} className={cn('min-h-[104px] border-b border-r border-token-subtle p-1.5', !inMonth && 'bg-canvas-subtle')}>
                  <div className="mb-1 flex items-center justify-between">
                    <button onClick={() => { setDate(k); setView('day') }} className={cn('flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold', isToday ? 'bg-brand text-brand-fg' : inMonth ? 'text-token-primary hover:bg-canvas-subtle' : 'text-token-muted')}>{d.getDate()}</button>
                  </div>
                  <div className="space-y-1">
                    {list.slice(0, 3).map((j) => { const t = statusTone(j.status ?? 'pending'); return (
                      <button key={j.id} onClick={() => navigate({ to: '/jobs/$id', params: { id: String(j.id) } })} className={cn('flex w-full items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-left', t.bg)}>
                        <span className={cn('font-display text-[10px] font-bold tabular-nums', t.text)}>{fmtTime(j.planned_start_date)}</span>
                        <span className={cn('truncate text-[10.5px] font-semibold', t.text)}>{j.name || 'Job'}</span>
                        <span className="ml-auto"><TechDot uid={j.user_id} /></span>
                      </button>
                    ) })}
                    {list.length > 3 && <button onClick={() => { setDate(k); setView('day') }} className="px-1.5 text-[10.5px] font-semibold text-token-secondary hover:text-token-primary">+{list.length - 3} more</button>}
                  </div>
                </div>
              )
            })}
          </div>
          </div>
        </div>
      ))}

      <EditJobModal job={edit} onClose={() => setEdit(null)} date={date} onSave={(start, dur) => { assign.mutate({ id: edit!.id, start, durationMin: dur, status: edit!.status }); setEdit(null) }} />
      <QuickAddModal open={adding} onClose={() => setAdding(false)} date={date} members={members} clients={clients} onAdd={async (p) => {
        // A rejected mutation here used to escape as an unhandled rejection: the modal stayed
        // open with no message, so a failed add looked exactly like a slow one.
        try { await quickAdd.mutateAsync(p); toast.success('Job added'); setAdding(false) }
        catch (e) { toast.error(e instanceof Error ? e.message : 'Could not add the job.') }
      }} />
    </Page>
  )
}

function EditJobModal({ job, onClose, date, onSave }: { job: ScheduleJob | null; onClose: () => void; date: string; onSave: (start: string, dur: number) => void }) {
  const [time, setTime] = useState('09:00')
  const [dur, setDur] = useState(60)
  const [seed, setSeed] = useState<number | null>(null)
  if (job && job.id !== seed) {
    setSeed(job.id)
    if (job.planned_start_date) { const d = new Date(job.planned_start_date); setTime(toInputTime(d)); if (job.planned_end_date) { const m = Math.round((new Date(job.planned_end_date).getTime() - d.getTime()) / 60000); setDur(m > 0 ? m : 60) } }
  }
  return (
    <Modal open={!!job} onClose={onClose} title={job?.name ?? 'Schedule job'}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} onClick={() => onSave(manilaWallToIso(date, time)!, Math.max(15, dur))}>Save time</button></>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start time"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
        <Field label="Duration (min)"><Input type="number" min={15} step={15} value={dur} onChange={(e) => setDur(e.target.valueAsNumber || 60)} /></Field>
      </div>
    </Modal>
  )
}

function QuickAddModal({ open, onClose, date, members, clients, onAdd }: {
  open: boolean; onClose: () => void; date: string
  members: Array<{ id: string; first_name: string | null; last_name: string | null; email: string | null }>
  clients: Array<{ id: string; first_name?: string | null; last_name?: string | null; organization?: string | null }>
  onAdd: (p: { name: string; user_id?: string | null; contact_id?: string | null; start?: string | null; durationMin?: number }) => void
}) {
  const [name, setName] = useState('')
  const [userId, setUserId] = useState('')
  const [contactId, setContactId] = useState('')
  const [time, setTime] = useState('09:00')
  const [dur, setDur] = useState(60)
  return (
    <Modal open={open} onClose={onClose} title="New job"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={!name.trim()} onClick={() => onAdd({ name, user_id: userId || null, contact_id: contactId || null, start: manilaWallToIso(date, time)!, durationMin: Math.max(15, dur) })}>Add job</button></>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Job title" className="col-span-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Switchboard upgrade" autoFocus /></Field>
        <Field label="Client" className="col-span-2">
          {/* Spec 040. use-clients loads EVERY contact (1,615 at a customer), so as a <select> this
              rendered a node per client and could not be searched. */}
          <Combobox
            value={contactId}
            onChange={setContactId}
            placeholder="Search clients…"
            options={clients.map((c) => ({
              value: c.id,
              label: clientName({ first_name: c.first_name ?? null, last_name: c.last_name ?? null, organization: c.organization ?? null }),
            }))}
          />
        </Field>
        <Field label="Assign to">
          <Combobox
            value={userId}
            onChange={setUserId}
            placeholder="Unassigned"
            options={members.map((m) => ({ value: m.id, label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || '' }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Start"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          <Field label="Min"><Input type="number" min={15} step={15} value={dur} onChange={(e) => setDur(e.target.valueAsNumber || 60)} /></Field>
        </div>
      </div>
    </Modal>
  )
}
